/**
 * Mobile Sticky Add to Cart
 * Uses IntersectionObserver to show/hide a sticky ATC button on mobile
 * when the native product form is out of viewport.
 */
if (!customElements.get('mobile-sticky-atc')) {
  class MobileStickyAtc extends HTMLElement {
    constructor() {
      super();
      this.productSection = this.closest('.cc-main-product') || this.closest('[data-section-type="product"]');
      this.productForm = this.productSection ? this.productSection.querySelector('product-form') : null;
      this.nativeSubmitBtn = this.productForm ? this.productForm.querySelector('[name="add"]') : null;
      this.stickyBtn = this.querySelector('.mobile-sticky-atc__btn');
      this.footer = document.querySelector('footer') || document.querySelector('.shopify-section-footer') || document.querySelector('#shopify-section-footer');
      this.cartDrawer = document.querySelector('cart-drawer');

      if (this.productForm && this.stickyBtn && 'IntersectionObserver' in window) {
        this.init();
      }
    }

    init() {
      this.formVisible = true;
      this.footerVisible = false;
      this.cartDrawerOpen = false;

      // Observe native product form visibility
      this.formObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.formVisible = entry.isIntersecting;
          this.updateVisibility();
        });
      }, { threshold: 0 });

      this.formObserver.observe(this.productForm);

      // Observe footer visibility
      if (this.footer) {
        this.footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.footerVisible = entry.isIntersecting;
            this.updateVisibility();
          });
        }, { threshold: 0 });

        this.footerObserver.observe(this.footer);
      }

      // Watch cart drawer open/close via aria-hidden attribute
      if (this.cartDrawer) {
        this.cartDrawerOpen = this.cartDrawer.getAttribute('aria-hidden') === 'false';

        this.cartDrawerObserver = new MutationObserver(() => {
          this.cartDrawerOpen = this.cartDrawer.getAttribute('aria-hidden') === 'false';
          this.updateVisibility();
        });

        this.cartDrawerObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      }

      // Bind click event on sticky button
      this.stickyBtn.addEventListener('click', this.handleClick.bind(this));

      // Sync button text/state with native button
      this.syncWithNativeBtn();
      this.observeNativeBtn();
    }

    updateVisibility() {
      const shouldShow = !this.formVisible && !this.footerVisible && !this.cartDrawerOpen;
      this.classList.toggle('mobile-sticky-atc--visible', shouldShow);
    }

    handleClick(e) {
      e.preventDefault();

      if (!this.nativeSubmitBtn || this.nativeSubmitBtn.disabled || this.nativeSubmitBtn.getAttribute('aria-disabled') === 'true') {
        // If native button is disabled (sold out), scroll to form
        this.productForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Add loading state
      this.stickyBtn.classList.add('is-loading');
      this.stickyBtn.disabled = true;

      // Trigger the native form submit
      this.nativeSubmitBtn.click();

      // Remove loading state after a delay
      setTimeout(() => {
        this.stickyBtn.classList.remove('is-loading');
        this.stickyBtn.disabled = false;
      }, 2500);
    }

    syncWithNativeBtn() {
      if (!this.nativeSubmitBtn) return;
      this.stickyBtn.textContent = this.nativeSubmitBtn.textContent.trim();
      this.stickyBtn.disabled = this.nativeSubmitBtn.disabled;
    }

    observeNativeBtn() {
      if (!this.nativeSubmitBtn) return;

      this.mutationObserver = new MutationObserver(() => {
        this.syncWithNativeBtn();
      });

      this.mutationObserver.observe(this.nativeSubmitBtn, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    disconnectedCallback() {
      if (this.formObserver) this.formObserver.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      if (this.cartDrawerObserver) this.cartDrawerObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
    }
  }

  customElements.define('mobile-sticky-atc', MobileStickyAtc);
}
